/**
 * Every user-facing analytics event, defined once, first-party first.
 *
 * The first-party `web_events` pipeline is the primary channel: each function names its
 * `event_type` and sends it unconditionally, because first-party data is Essential and is never
 * gated on tracking consent. Reporting the same action to GTM/GA4 is secondary and happens in the
 * same function, under Google's own event name and payload shape — Google mandates those names
 * (`view_item`, `add_to_cart`, `purchase`) and they deliberately do not reach the first-party
 * schema, where the naming convention is plain snake_case.
 *
 * Three shapes appear below on purpose:
 *   - both channels     — the usual case.
 *   - first-party only  — no GA4 equivalent is wanted.
 *   - GA4 only          — the first-party side of that action is already covered by a different
 *     event, so sending one here would double-count it. Each such function names its covering
 *     event. This replaced a central `GA4_SUPERSEDED` list that had to be kept in sync from the
 *     GTM side of the codebase.
 *
 * Property names are snake_case on the first-party side even where GA4's copy is camelCase: the
 * first-party events land in BigQuery, where a mixed-case column is a permanent query hazard.
 */

import type { CinemaPaywallFlow } from "./cinema-pay-first";
import { pushToDataLayer } from "./gtm";
import { readLpCampaign } from "./lp-campaign";
import { trackWebEvent } from "./web-analytics";

type DownloadTargetOs = "ios" | "android";
type TrialToggleStatus = "enabled" | "disabled";
type PaymentType = "stripe" | "free_order";
type VideoEvent =
  | "video_enter"
  | "video_first_frame"
  | "video_start"
  | "video_complete"
  | "video_reward"
  | "video_stall"
  | "video_stall_end";
type NavigationType = "browser_history_change" | "react_router_navigation";

/**
 * GA4's mandated ecommerce payload. Prices reach us in cents and GA4 wants major units, so the
 * conversion happens here rather than at each call site.
 */
function ga4Ecommerce(
  event: string,
  currency: string,
  priceInCents: number,
  productId: string,
  productName: string,
  skuId: string | undefined
) {
  const value = priceInCents / 100;
  return {
    event,
    ecommerce: {
      currency,
      value,
      items: [{ item_id: productId, item_name: productName, item_variant: skuId, price: value }],
    },
  };
}

/**
 * Extracts the container format from a video URL, e.g. "mp4" or "m3u8".
 *
 * The extension is reported verbatim (lowercased) rather than mapped onto a fixed set, so a format
 * we start serving later shows up in reports on its own instead of being folded into a catch-all
 * bucket. "unknown" is used only when the URL carries no usable extension at all.
 */
function videoFormat(videoUrl: string): string {
  const path = videoUrl.split(/[?#]/)[0];
  const filename = path.slice(path.lastIndexOf("/") + 1);
  const dotIndex = filename.lastIndexOf(".");
  if (dotIndex === -1) return "unknown";

  const extension = filename.slice(dotIndex + 1).toLowerCase();
  return /^[a-z0-9]+$/.test(extension) ? extension : "unknown";
}

// ============================================================================
// Identity and authentication
// ============================================================================

/**
 * GA4 only — the first-party pipeline reads the user from the store on every event, so it needs no
 * separate identify call. This is the single place `user_id` reaches the dataLayer, so login,
 * bootstrap and logout cannot drift.
 */
export function trackUserIdentified(userId: string | null) {
  pushToDataLayer({ user_id: userId });
}

export function trackSignUp(signupMethod: string) {
  trackWebEvent({ event_type: "sign_up", signup_method: signupMethod });
  pushToDataLayer({ event: "sign_up", signup_method: signupMethod });
}

export function trackAuthenticated() {
  trackWebEvent({ event_type: "authenticated" });
  pushToDataLayer({ event: "authenticated" });
}

// ============================================================================
// Navigation
// ============================================================================

/**
 * Browser back/forward navigation. `route` and `url` already ride in every first-party envelope, so
 * the first-party event carries only what the envelope cannot say: which kind of navigation this
 * was, and the history state it restored.
 */
export function trackPageNavigation(
  navigationType: NavigationType,
  pathname: string,
  search?: string,
  hash?: string,
  state?: unknown
) {
  const navigationState = state && navigationType === "react_router_navigation" ? JSON.stringify(state) : undefined;

  trackWebEvent({ event_type: "page_navigation", navigation_type: navigationType, navigation_state: navigationState });

  const gtmEvent: Record<string, any> = {
    event: "page_navigation",
    navigationType,
    pageLocation: `${pathname}${search || ""}${hash || ""}`,
    pagePath: pathname,
  };
  if (search) gtmEvent.pageSearch = search;
  if (hash) gtmEvent.pageHash = hash;
  if (navigationState) gtmEvent.navigationState = navigationState;
  pushToDataLayer(gtmEvent);
}

// ============================================================================
// App download
// ============================================================================

/** First-party only: opening the download-app modal has no GA4 counterpart. */
export function trackDownloadSelect() {
  trackWebEvent({ event_type: "download_select" });
}

/** First-party only. */
export function trackDownloadStart(targetOs: DownloadTargetOs) {
  trackWebEvent({ event_type: "download_start", target_os: targetOs });
}

// ============================================================================
// Deal browsing
// ============================================================================

/** First-party only. */
export function trackDealLoadMore(properties: { channel?: string; loaded_count: number; has_more: boolean }) {
  trackWebEvent({ event_type: "deal_load_more", ...properties });
}

/** First-party only. */
export function trackDealClick(properties: {
  deal_id: string;
  product_id?: string;
  product_name?: string;
  position?: number;
  channel?: string;
  price?: number | null;
}) {
  trackWebEvent({ event_type: "deal_click", ...properties });
}

/**
 * Viewing a deal's detail page, with the product it is about.
 *
 * Separate from the route's page view because it carries the product and its price, which the
 * page-view envelope does not and should not: that envelope is the same shape on every route.
 */
export function trackDealDetailView(
  currency: string,
  priceInCents: number,
  productId: string,
  productName: string,
  skuId: string | undefined
) {
  trackWebEvent({
    event_type: "deal_detail_view",
    currency,
    price_in_cents: priceInCents,
    product_id: productId,
    product_name: productName,
    sku_id: skuId,
  });
  pushToDataLayer(ga4Ecommerce("view_item", currency, priceInCents, productId, productName, skuId));
}

// ============================================================================
// Checkout
// ============================================================================

/**
 * The Buy Now click: first-party `deal_checkout`, reported to GA4 as `add_to_cart`.
 *
 * `price_in_cents` is optional because GA4's ecommerce payload is meaningless without a price,
 * while the first-party event is still worth recording without one. Omitting it therefore sends
 * the first-party event alone, which is what the deal page did before these two were one call.
 */
export function trackDealCheckout(properties: {
  deal_id: string;
  product_id: string;
  product_name: string;
  sku_id?: string;
  buy_source?: string;
  req_id?: string;
  currency: string;
  price_in_cents?: number;
  two_buttons_choice?: string;
}) {
  // price_in_cents reaches the first-party event too: the backend reports this as Meta's AddToCart,
  // and a cart value is what makes that event usable for bidding rather than just a count.
  trackWebEvent({ event_type: "deal_checkout", ...properties });

  const { price_in_cents } = properties;
  if (price_in_cents !== undefined) {
    pushToDataLayer(
      ga4Ecommerce(
        "add_to_cart",
        properties.currency,
        price_in_cents,
        properties.product_id,
        properties.product_name,
        properties.sku_id
      )
    );
  }
}

/**
 * GA4 only — first-party covers the checkout page with `page_view` on route `/deal/checkout`.
 */
export function trackCheckoutStart(
  currency: string,
  priceInCents: number,
  productId: string,
  productName: string,
  skuId: string | undefined
) {
  pushToDataLayer(ga4Ecommerce("begin_checkout", currency, priceInCents, productId, productName, skuId));
}

/**
 * GA4 only — reported when a shipping address is saved from inside the checkout flow.
 *
 * Deliberately NOT merged with {@link trackShippingInfoSubmit}: that one fires from AddressForm,
 * which is also mounted on the account settings page, so it answers "an address was saved
 * anywhere" while this answers "the checkout flow reached its shipping step".
 */
export function trackCheckoutShippingInfo(
  currency: string,
  priceInCents: number,
  productId: string,
  productName: string,
  skuId: string | undefined
) {
  pushToDataLayer(ga4Ecommerce("add_shipping_info", currency, priceInCents, productId, productName, skuId));
}

/**
 * First-party only. Fires when the address form passes client validation, before the guest signup /
 * save round-trip runs, so drop-off during that round-trip is measurable.
 */
export function trackAddressFormSubmit(properties: {
  is_guest: boolean;
  has_existing_address: boolean;
  country: string;
}) {
  trackWebEvent({ event_type: "address_form_submit", ...properties });
}

/** First-party only. Fires wherever AddressForm is used, checkout and account settings alike. */
export function trackShippingInfoSubmit(properties: {
  is_guest: boolean;
  has_existing_address: boolean;
  country: string;
}) {
  trackWebEvent({ event_type: "shipping_info_submit", ...properties });
}

/** First-party only. */
export function trackTrialToggleClick(status: TrialToggleStatus) {
  trackWebEvent({ event_type: "trial_toggle_click", status });
}

/** First-party only. */
export function trackPaymentMethodSelect(properties: {
  currency: string;
  item_count: number;
  payment_type: PaymentType;
  trial_enabled: boolean;
}) {
  trackWebEvent({ event_type: "payment_method_select", ...properties });
}

// ============================================================================
// Payment outcome
// ============================================================================

/**
 * First-party only: every completed order, paid or free.
 *
 * Deliberately NOT merged with {@link trackPurchase}: a free order must not land in GA4 as a
 * `purchase` conversion, so the two channels genuinely have different membership here. Merging
 * them would need a per-caller "also report to Google" flag, which is the signal to keep them
 * apart rather than to add the flag.
 */
export function trackOrderComplete(properties: {
  order_id?: string;
  currency: string;
  value?: number;
  item_count: number;
  payment_type: PaymentType;
  trial_enabled: boolean;
  deal_id?: string;
  product_id?: string;
  product_name?: string;
  sku_id?: string;
}) {
  trackWebEvent({ event_type: "order_complete", ...properties });
}

/**
 * The paid-order conversion.
 *
 * Deliberately separate from {@link trackOrderComplete}, which fires for free orders too: a free
 * order must not be reportable as a sale, so the two cannot share one event name.
 *
 * `priceInCents` is what makes this a sale rather than a count, so a zero emits nothing first-party:
 * a zero-valued conversion is still a number that gets bid and billed on, and an order whose price
 * did not survive the trip is better left unreported than reported as free.
 */
export function trackPurchase(
  currency: string,
  priceInCents: number,
  productId: string,
  productName: string,
  skuId: string | undefined
) {
  if (priceInCents > 0) {
    trackWebEvent({
      event_type: "purchase",
      currency,
      price_in_cents: priceInCents,
      product_id: productId,
      product_name: productName,
      sku_id: skuId,
    });
  }
  pushToDataLayer(ga4Ecommerce("purchase", currency, priceInCents, productId, productName, skuId));
}

/**
 * Membership trial started. Both channels use `start_trial`.
 *
 * This is the one event that keeps a GA4-shaped name on the first-party side. It is already plain
 * snake_case, so it breaks no convention, and it has backfilled history under that name
 * (`web_events_posthog_backfill.sql`, executed 2026-08-28) that `web_events_funnel.sql` reads.
 * Renaming it would buy nothing but a tidier name and cost a production rewrite of that history.
 */
export function trackStartTrial(currency: string, trialDuration?: string) {
  const subscription = {
    subscription_product_id: "blidz_plus_trial",
    subscription_price: 0,
    subscription_currency: currency,
    ...(trialDuration !== undefined ? { trial_duration: trialDuration } : {}),
  };

  trackWebEvent({ event_type: "start_trial", ...subscription });
  pushToDataLayer({ event: "start_trial", ...subscription });
}

/**
 * Payment state machine transitions, for debugging stuck checkouts.
 *
 * `additional_data` stays a nested object on the GA4 side but is spread flat into the first-party
 * event: BigQuery cannot filter into a nested blob. The explicit fields are written after the
 * spread so a stray key in `additionalData` cannot overwrite the state itself.
 */
export function trackPaymentStateChange(
  currentState: string,
  previousState: string | null,
  phase: "begin" | "end",
  additionalData?: Record<string, any>
) {
  trackWebEvent({
    ...additionalData,
    event_type: "payment_state_change",
    payment_state: currentState,
    state_phase: phase,
    previous_state: previousState,
  });

  const gtmEvent: Record<string, any> = {
    event: "payment_state_change",
    payment_state: currentState,
    state_phase: phase,
    timestamp: new Date().toISOString(),
  };
  if (previousState) gtmEvent.previous_state = previousState;
  if (additionalData) gtmEvent.additional_data = additionalData;
  pushToDataLayer(gtmEvent);
}

// ============================================================================
// Watch feed video
// ============================================================================

/**
 * Video engagement on the watch surfaces.
 *
 * Every event carries `video_url` and `video_format`. `video_format` is the dashboard dimension —
 * load timings differ a lot between formats, so any latency breakdown should split on it.
 * `video_url` is for identifying which clip a data point came from when investigating; it is
 * high-cardinality and not meant for dashboards.
 *
 * @param msSinceEnter - Milliseconds elapsed since `video_enter` for this item, measured fresh at
 *   each event (0 on `video_enter`). Omitted only when the item was never entered.
 * @param firstFrameSource - Only for `video_first_frame`: "rvfc" when the frame was observed
 *   painted via `requestVideoFrameCallback`, "loadeddata" when it was observed decoded via the
 *   `loadeddata` event. Split on this before comparing timings — the two mean different things.
 */
export function trackVideoEvent(
  event: VideoEvent,
  feedItemId: string,
  videoUrl: string,
  contentType: "product" | "entertainment",
  surface: string,
  msSinceEnter?: number,
  firstFrameSource?: "rvfc" | "loadeddata"
) {
  const properties: Record<string, any> = {
    feed_item_id: feedItemId,
    video_url: videoUrl,
    video_format: videoFormat(videoUrl),
    content_type: contentType,
    surface,
  };
  if (msSinceEnter !== undefined) properties.ms_since_enter = msSinceEnter;
  if (firstFrameSource !== undefined) properties.first_frame_source = firstFrameSource;

  trackWebEvent({ event_type: event, ...properties });
  pushToDataLayer({ event, ...properties });
}

// ============================================================================
// Marketing landing pages
// ============================================================================

/**
 * Landing-page events, all first-party only. None reaches GA4: a download CTA is a store-click, not
 * an install, and install attribution is AppsFlyer's (server-side), so forwarding a click to the ad
 * platform would risk being counted as a conversion it is not.
 */
export function trackLpView(lpVariant: string) {
  trackWebEvent({ event_type: "lp_view", ...readLpCampaign(lpVariant) });
}

export function trackLpStepView(lpVariant: string, step: string) {
  trackWebEvent({ event_type: "lp_step_view", step, ...readLpCampaign(lpVariant) });
}

export function trackLpCtaClick(lpVariant: string, ctaId: string) {
  trackWebEvent({ event_type: "lp_cta_click", cta_id: ctaId, ...readLpCampaign(lpVariant) });
}

// ============================================================================
// Cinema
// ============================================================================

/**
 * The cinema funnel, first-party only. The page's url already carries the series and episode, but
 * the funnel steps name them too: they are what the funnel is read by, and a column beats parsing a
 * query string in BigQuery.
 */
export function trackCinemaHomeView() {
  trackWebEvent({ event_type: "cinema_home_view" });
}

export function trackCinemaSeriesView(seriesId: string) {
  trackWebEvent({ event_type: "series_view", series_id: seriesId });
}

export function trackCinemaEpisodePlay(seriesId: string, episode: number) {
  trackWebEvent({ event_type: "ep_play", series_id: seriesId, episode });
}

export function trackCinemaGateView(seriesId: string, episode: number, flow: CinemaPaywallFlow | null) {
  trackWebEvent({ event_type: "gate_view", series_id: seriesId, episode, flow });
}

export function trackCinemaLoginDone(seriesId: string, episode: number) {
  trackWebEvent({ event_type: "login_done", series_id: seriesId, episode });
}

export function trackCinemaPaywallView(seriesId: string, episode: number, flow: CinemaPaywallFlow | null) {
  trackWebEvent({ event_type: "paywall_view", series_id: seriesId, episode, flow });
}

export function trackCinemaPaywallSaveView(seriesId: string, episode: number) {
  trackWebEvent({ event_type: "paywall_save_view", series_id: seriesId, episode });
}

export function trackCinemaPaywallSubmit(paymentMethod: string | undefined, flow: CinemaPaywallFlow | null) {
  trackWebEvent({ event_type: "paywall_submit", payment_method: paymentMethod ?? null, flow });
}

export function trackCinemaPaywallLoginClick() {
  trackWebEvent({ event_type: "paywall_login_click" });
}

/**
 * Every method the Payment Element offers, cards included. Stripe names them `applePay` here but `apple_pay`
 * in the change event's `value.type`, which `paywall_submit` records; one spelling keeps the two joinable.
 */
export function trackCinemaPaywallMethodsReady(stripeNames: string[], flow: CinemaPaywallFlow | null) {
  const methods = stripeNames.map(name => name.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`));
  trackWebEvent({ event_type: "paywall_methods_ready", methods: methods.join(",") || "none", flow });
}

export function trackCinemaPaywallExistingEmail(paymentMethod: string) {
  trackWebEvent({ event_type: "paywall_existing_email", payment_method: paymentMethod });
}

export function trackCinemaPaywallCodeVerified() {
  trackWebEvent({ event_type: "paywall_code_verified" });
}

export function trackCinemaPaywallMemberSkip(seriesId: string, episode: number) {
  trackWebEvent({ event_type: "paywall_member_skip", series_id: seriesId, episode });
}

/** `code` is Stripe's decline or error code where Stripe gave one: what a declined card is counted by. */
export function trackCinemaPaywallError(message: string, code?: string, paymentMethod?: string) {
  trackWebEvent({
    event_type: "paywall_error",
    error_message: message,
    error_code: code ?? null,
    payment_method: paymentMethod ?? null,
  });
}

export function trackCinemaTrialActivationFailed(
  reason: "failed" | "timeout" | "not_saved" | "unchecked",
  message?: string
) {
  trackWebEvent({ event_type: "trial_activation_failed", reason, error_message: message ?? null });
}
